/**
 * Utilitários para edges do React Flow (diagramas caixa/armário).
 * Curvatura e path customizado para conexões entre FOs (fusão no mesmo nó = arco para fora).
 */

export interface CurvatureOptions {
  sourceNodeId?: string;
  targetNodeId?: string;
}

/**
 * Curvatura para conexões entre FOs (entre nós diferentes).
 * Fusão no mesmo nó usa getFusionEdgePath em vez do bezier.
 */
export function getCurvatureForCableFo(
  sourceHandle: string | null | undefined,
  targetHandle: string | null | undefined,
  options?: CurvatureOptions
): number | undefined {
  const isSameNode = options?.sourceNodeId != null && options?.targetNodeId != null && options.sourceNodeId === options.targetNodeId;
  if (isSameNode) return undefined;

  const foSource = sourceHandle?.match(/(?:output|input)-fo(\d+)/i)?.[1];
  const foTarget = targetHandle?.match(/(?:output|input)-fo(\d+)/i)?.[1];
  if (foSource == null || foTarget == null) return undefined;
  const a = parseInt(foSource, 10);
  const b = parseInt(foTarget, 10);
  const distance = Math.abs(a - b);
  return Math.min(0.95, 0.15 + distance * 0.1);
}

/**
 * Path em arco para fusão no mesmo nó (ex.: output-fo3 → input-fo11 no caboin/cabo passagem).
 * O arco sai para fora do nó: handles à direita = arco à direita; handles à esquerda = arco à esquerda.
 * Retorna [pathString, labelX, labelY] no mesmo formato de getBezierPath.
 */
export function getFusionEdgePath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  sourceHandleId?: string | null,
  targetHandleId?: string | null,
  /** Quando true (cabo de passagem, handles à esquerda), arco para a esquerda; senão arco para a direita */
  arcToLeft?: boolean
): [string, number, number] {
  const foSource = sourceHandleId?.match(/(?:output|input)-fo(\d+)/i)?.[1];
  const foTarget = targetHandleId?.match(/(?:output|input)-fo(\d+)/i)?.[1];
  const distance = (foSource != null && foTarget != null)
    ? Math.abs(parseInt(foSource, 10) - parseInt(foTarget, 10))
    : Math.abs(targetY - sourceY) / 10;

  // Deslocamento para fora do nó (direita ou esquerda conforme handles)
  const offsetMagnitude = 35 + Math.min(65, distance * 10);
  const offset = arcToLeft ? -offsetMagnitude : offsetMagnitude;

  const c1x = sourceX + offset;
  const c1y = sourceY;
  const c2x = targetX + offset;
  const c2y = targetY;

  const path = `M ${sourceX} ${sourceY} C ${c1x} ${c1y} ${c2x} ${c2y} ${targetX} ${targetY}`;

  // Ponto médio aproximado da curva (t=0.5 em Bézier cúbico)
  const t = 0.5;
  const mt = 1 - t;
  const labelX = mt * mt * mt * sourceX + 3 * mt * mt * t * c1x + 3 * mt * t * t * c2x + t * t * t * targetX;
  const labelY = mt * mt * mt * sourceY + 3 * mt * mt * t * c1y + 3 * mt * t * t * c2y + t * t * t * targetY;

  return [path, labelX, labelY];
}
